// useRouteLeaveGuard — the dirty guard's ROUTE-LEAVE half (ADR-038 §3): blocks
// in-app navigation while a form is dirty and routes the attempt through the
// shell's discard prompt. The page / inline-section / wizard containers mount it;
// the drawer doesn't (it never changes the route).
//
// Rides React Router's useBlocker, which requires a DATA router (createBrowserRouter
// / RouterProvider) — it THROWS under a plain <BrowserRouter>/<MemoryRouter>. That
// is why this is a separate hook, not baked into useFormShell (the shell must also
// render in Workbench stories under the MemoryRouter decorator). The app router is
// a data router; the Workbench route-leave stories opt in via
// `parameters: { router: 'data' }` (.storybook/preview.tsx).

import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import type { FieldValues } from 'react-hook-form';
import type { UseFormShellReturn } from './useFormShell.js';

export interface RouteLeaveGuard {
  /** True while an in-app navigation is held, pending the discard decision. */
  blocked: boolean;
  /** Discard + proceed with the held navigation. */
  proceed: () => void;
  /** Keep editing — cancel the held navigation. */
  reset: () => void;
}

/** Block in-app navigation (pathname change) while `when` is true. */
export function useRouteLeaveGuard(when: boolean): RouteLeaveGuard {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  );

  // Latch — `proceed()` fires AT MOST ONCE per blocked episode. React
  // StrictMode double-invokes effects within one commit (and the post-submit
  // reset→navigate path calls proceed via the discard handshake), where
  // `blocker.state` still reads 'blocked' on the 2nd call because React Router's
  // transition to 'proceeding' hasn't re-rendered yet — a 2nd `blocker.proceed()`
  // then throws `Invalid blocker state transition: proceeding -> proceeding`. The
  // ref mutates synchronously, so the same-commit 2nd call sees it set and skips;
  // the effect clears it once the blocker leaves 'blocked' (ready for next time).
  const proceeded = useRef(false);
  useEffect(() => {
    if (blocker.state !== 'blocked') proceeded.current = false;
  }, [blocker.state]);

  const proceed = useCallback(() => {
    if (blocker.state === 'blocked' && !proceeded.current) {
      proceeded.current = true;
      blocker.proceed();
    }
  }, [blocker]);

  // If the guard turns off (saved / discarded) while a navigation is held, release
  // it so the user isn't stranded on a page they asked to leave.
  useEffect(() => {
    if (!when) proceed();
  }, [when, proceed]);

  return {
    blocked: blocker.state === 'blocked',
    proceed,
    reset: () => {
      // A proceed is in flight — the dialog's close-after-confirm must NOT
      // cancel it. `blocker.state` still reads 'blocked' synchronously after
      // `blocker.proceed()` (no re-render yet), so without this latch check a
      // ConfirmDialog wired `onConfirm={proceed} onClose={reset}` silently
      // cancels its own discard (the import wizard's ✕ → Discard → nothing).
      if (proceeded.current) return;
      if (blocker.state === 'blocked') blocker.reset();
    },
  };
}

/**
 * Bridge the route blocker into a shell's discard prompt. Mount once in a page /
 * inline / wizard container: when navigation is attempted while the form is dirty,
 * the shell's discard ConfirmDialog opens — Discard proceeds the navigation (and
 * resets the form), Keep editing cancels it.
 */
export function useFormRouteLeave<TValues extends FieldValues>(
  shell: UseFormShellReturn<TValues>,
): void {
  const guard = useRouteLeaveGuard(shell.isDirty);
  useEffect(() => {
    if (guard.blocked) shell.requestLeave(guard.proceed, guard.reset);
    // Fire only on the transition into `blocked`; the closure captures this
    // render's guard/shell, which are current at that transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guard.blocked]);
}
